import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Calendar, Clock, ArrowLeft, Check, User, ChevronRight, AlertTriangle, Plus, Minus, Filter, Star, CreditCard, Store, BellRing, X, CalendarClock, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { useAuth, api } from '../contexts/AuthContext';
import { StripePaymentModal } from '../components/StripePaymentModal';
import { useLanguage } from '../i18n';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { pt, enGB } from 'date-fns/locale';
// ── LISTA DE ESPERA · Fase 1 ───────────────────────────────────────
// Independent modal: only POST /waitlist and GET /waitlist/config.
// Does not create bookings, holds, payments or outgoing notifications.
const addDaysLocal = (value, days) => {
  const d = new Date(value.getFullYear(), value.getMonth(), value.getDate() + days);
  return format(d, 'yyyy-MM-dd');
};
const WaitlistJoinModal = ({ open, onClose, selectedServices, selectedStaff, selectedDate, eligibleStaff, user, language }) => {
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [periods, setPeriods] = useState(['any']);
  const [earliestTime, setEarliestTime] = useState('');
  const [latestTime, setLatestTime] = useState('');
  const [staffPreference, setStaffPreference] = useState('specific');
  const [selectedStaffForRequest, setSelectedStaffForRequest] = useState('');
  const [whatsappOptIn, setWhatsappOptIn] = useState(false);
  const [emailOptIn, setEmailOptIn] = useState(true);
  const [requestNotes, setRequestNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null);
  const [config, setConfig] = useState(null);
  const [configError, setConfigError] = useState(false);
  const ptPT = language === 'pt';
  useEffect(() => {
    if (!open) return;
    const start = selectedDate instanceof Date && selectedDate >= new Date(new Date().setHours(0, 0, 0, 0)) ? selectedDate : new Date();
    setDateFrom(addDaysLocal(start, 0));
    setDateTo(addDaysLocal(start, 7) > addDaysLocal(new Date(), 120) ? addDaysLocal(new Date(), 120) : addDaysLocal(start, 7));
    setPeriods(['any']);
    setEarliestTime('');
    setLatestTime('');
    setStaffPreference(selectedStaff ? 'specific' : 'any');
    setSelectedStaffForRequest(selectedStaff || '');
    setWhatsappOptIn(false);
    setEmailOptIn(!!user?.email);
    setRequestNotes('');
    setSaving(false);
    setCreated(null);
    setConfig(null);
    setConfigError(false);
    let active = true;
    api.get('/waitlist/config')
      .then(r => { if (active) setConfig(r.data); })
      .catch(() => { if (active) setConfigError(true); });
    return () => { active = false; };
  }, [open, selectedStaff, selectedDate, user?.email]);
  useEffect(() => {
    if (!open) return;
    const closeWithEscape = e => { if (e.key === 'Escape' && !saving) onClose(); };
    window.addEventListener('keydown', closeWithEscape);
    return () => window.removeEventListener('keydown', closeWithEscape);
  }, [open, onClose, saving]);
  // Impede que o conteúdo por trás do modal se mova no Safari do iPhone.
  useEffect(() => {
    if (!open || !window.matchMedia('(max-width: 639px)').matches) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, [open]);
  const togglePeriod = p => setPeriods(current => {
    if (p === 'any') return ['any'];
    const next = current.includes(p) ? current.filter(v => v !== p) : [...current.filter(v => v !== 'any'), p];
    return next.length ? next : ['any'];
  });
  const submit = async e => {
    e.preventDefault();
    if (saving || created) return;
    if (configError || !config) { toast.error(ptPT ? 'Não foi possível validar a configuração da lista de espera.' : 'Could not verify the waitlist configuration.'); return; }
    if (!dateFrom || !dateTo || dateFrom > dateTo) { toast.error(ptPT ? 'Verifica o intervalo de datas.' : 'Check the date range.'); return; }
    if (new Date(dateTo + 'T12:00:00') > new Date(addDaysLocal(new Date(), 120) + 'T12:00:00')) { toast.error(ptPT ? 'Escolhe datas dentro dos próximos 120 dias.' : 'Choose dates within 120 days.'); return; }
    if ((new Date(dateTo + 'T00:00:00Z') - new Date(dateFrom + 'T00:00:00Z')) / 86400000 > 90) { toast.error(ptPT ? 'O intervalo pode ter até 91 dias.' : 'The date range can cover up to 91 days.'); return; }
    if (earliestTime && latestTime && earliestTime > latestTime) { toast.error(ptPT ? 'A hora inicial não pode ser posterior à hora final.' : 'Start time cannot be after end time.'); return; }
    if (!emailOptIn && !whatsappOptIn) { toast.error(ptPT ? 'Seleciona pelo menos um canal de aviso.' : 'Select at least one notification channel.'); return; }
    if (whatsappOptIn && !user?.phone?.trim()) { toast.error(ptPT ? 'Adiciona um telemóvel ao teu perfil para receber WhatsApp.' : 'Add a phone number to your profile for WhatsApp.'); return; }
    if (emailOptIn && !user?.email?.trim()) { toast.error(ptPT ? 'É necessário um email no perfil para este aviso.' : 'An email address is required for email alerts.'); return; }
    const serviceIds = selectedServices.map(s => s.service_id);
    if (serviceIds.length < 1 || serviceIds.length > 6) { toast.error(ptPT ? 'Escolhe entre 1 e 6 serviços para a lista de espera.' : 'Choose 1–6 services for the waitlist.'); return; }
    setSaving(true);
    try {
      const response = await api.post('/waitlist', {
        service_ids: serviceIds,
        staff_id: staffPreference === 'specific' ? selectedStaffForRequest || null : null,
        date_from: dateFrom,
        date_to: dateTo,
        periods,
        earliest_time: earliestTime || null,
        latest_time: latestTime || null,
        notes: requestNotes.trim() || null,
        whatsapp_opt_in: whatsappOptIn,
        email_opt_in: emailOptIn,
      });
      setCreated(response.data);
      toast.success(ptPT ? 'Pedido de lista de espera guardado!' : 'Waitlist request saved!');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : Array.isArray(detail) ? detail.map(d => d.msg).filter(Boolean).join('; ') : null;
      toast.error(msg || (ptPT ? 'Não foi possível guardar o pedido.' : 'Could not save your request.'));
    } finally { setSaving(false); }
  };
  if (!open) return null;
  const todayStr = addDaysLocal(new Date(), 0);
  const maxDate = addDaysLocal(new Date(), 120);
  const periodOptions = [
    { key: 'any', pt: 'Qualquer horário', en: 'Any time' },
    { key: 'morning', pt: 'Manhã (até 12h)', en: 'Morning (before 12)' },
    { key: 'afternoon', pt: 'Tarde (12h–17h)', en: 'Afternoon (12–17)' },
    { key: 'evening', pt: 'Fim do dia (após 17h)', en: 'Evening (after 17)' },
  ];
  return (
    <div className="fixed inset-0 z-[100] flex items-end justify-center overflow-hidden bg-black/60 sm:items-center sm:p-4" role="presentation" onMouseDown={e => { if (e.target === e.currentTarget && !saving) onClose(); }}>
      <div role="dialog" aria-modal="true" aria-labelledby="waitlist-title" className="flex h-[100dvh] max-h-[100dvh] min-h-0 w-full min-w-0 flex-col overflow-hidden rounded-none bg-[#F9F8F6] shadow-2xl sm:h-auto sm:max-h-[92dvh] sm:max-w-xl sm:rounded-2xl">
        <div className="z-10 flex shrink-0 items-start justify-between gap-3 border-b border-[#E6E2DA] bg-[#F9F8F6] px-4 py-3 sm:px-5 sm:py-4">
          <div>
            <div className="flex items-center gap-2 text-[#BFA05F]"><BellRing className="h-5 w-5" /><span className="text-xs font-semibold uppercase tracking-wider">Lou's Way</span></div>
            <h2 id="waitlist-title" className="mt-1 font-heading text-2xl font-medium text-[#1A1815]">{ptPT ? 'Lista de espera' : 'Waitlist'}</h2>
            <p className="mt-1 text-sm text-[#635E54]">{ptPT ? 'Diz-nos quando podes vir. Guardaremos as tuas preferências.' : 'Tell us when you can visit. We will save your preferences.'}</p>
          </div>
          <button type="button" aria-label={ptPT ? 'Fechar' : 'Close'} disabled={saving} onClick={onClose} className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-[#635E54] hover:bg-[#E6E2DA]"><X className="h-5 w-5" /></button>
        </div>
        {created ? (
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-8 text-center sm:px-5" data-testid="waitlist-created">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-green-100"><Check className="h-7 w-7 text-green-700" /></div>
            <h3 className="text-xl font-medium text-[#1A1815]">{ptPT ? 'Pedido registado!' : 'Request saved!'}</h3>
            <p className="text-sm leading-relaxed text-[#635E54]">{ptPT ? 'As tuas preferências foram guardadas. Isto ainda não é uma marcação. Os avisos automáticos serão disponibilizados quando a funcionalidade estiver concluída.' : 'Your preferences were saved. This is not a booking. Automated alerts will become available when the feature is completed.'}</p>
            <Button type="button" onClick={onClose} className="min-h-[44px] w-full rounded-full bg-[#1A1815] px-8 text-white sm:w-auto">{ptPT ? 'Fechar' : 'Close'}</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="min-h-0 flex-1 space-y-5 overflow-y-auto overscroll-contain px-4 pb-0 pt-4 [-webkit-overflow-scrolling:touch] sm:px-5 sm:pt-5" data-testid="waitlist-form">
            {configError ? <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800">{ptPT ? 'A lista de espera não está disponível neste momento. Tenta novamente mais tarde.' : 'The waitlist is unavailable right now. Please try again later.'}</div> : !config ? <p className="text-sm text-[#635E54]">{ptPT ? 'A verificar a lista de espera…' : 'Checking waitlist…'}</p> : !config.auto_offers_enabled ? (
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"><strong>{ptPT ? 'Fase de testes.' : 'Testing phase.'}</strong> {ptPT ? 'O pedido fica guardado, mas ainda não são enviados avisos automáticos de vagas.' : 'Your request is saved, but automatic vacancy alerts are not yet being sent.'}</div>
            ) : null}
            <div className="rounded-xl border border-[#E6E2DA] bg-white p-4">
              <label className="mb-2 block text-sm font-medium text-[#1A1815]">{ptPT ? 'Serviços pretendidos' : 'Requested services'}</label>
              <ul className="space-y-1 text-sm text-[#635E54]">{selectedServices.map(s => <li key={s.service_id}>• {ptPT ? s.name_pt : s.name_en || s.name_pt}</li>)}</ul>
              <p className="mt-2 text-xs text-[#635E54]">{ptPT ? 'A ordem e a duração dos serviços serão respeitadas na procura.' : 'Service order and durations will be considered when matching.'}</p>
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-[#1A1815]">{ptPT ? 'Qual profissional preferes?' : 'Preferred professional'}</label>
              <select value={staffPreference === 'any' ? 'any' : selectedStaffForRequest || 'any'} onChange={e => { setStaffPreference(e.target.value === 'any' ? 'any' : 'specific'); setSelectedStaffForRequest(e.target.value === 'any' ? '' : e.target.value); }} className="w-full rounded-xl border border-[#E6E2DA] bg-white p-3 text-base sm:text-sm text-[#1A1815]">
                <option value="any">{ptPT ? 'Qualquer profissional habilitada' : 'Any qualified professional'}</option>
                {eligibleStaff.map(s => <option key={s.staff_id} value={s.staff_id}>{s.name}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <label className="block text-sm font-medium text-[#1A1815]">{ptPT ? 'De' : 'From'}
                <input aria-label={ptPT ? 'Data inicial' : 'Start date'} required type="date" min={todayStr} max={maxDate} value={dateFrom} onChange={e => { setDateFrom(e.target.value); if (dateTo < e.target.value) setDateTo(e.target.value); }} className="mt-2 w-full rounded-xl border border-[#E6E2DA] bg-white p-3 text-base sm:text-sm" />
              </label>
              <label className="block text-sm font-medium text-[#1A1815]">{ptPT ? 'Até' : 'To'}
                <input aria-label={ptPT ? 'Data final' : 'End date'} required type="date" min={dateFrom || todayStr} max={maxDate} value={dateTo} onChange={e => setDateTo(e.target.value)} className="mt-2 w-full rounded-xl border border-[#E6E2DA] bg-white p-3 text-base sm:text-sm" />
              </label>
            </div>
            <fieldset>
              <legend className="mb-2 text-sm font-medium text-[#1A1815]">{ptPT ? 'Períodos em que podes vir' : 'Times you are available'}</legend>
              <div className="grid grid-cols-2 gap-2">{periodOptions.map(p => <button type="button" key={p.key} aria-pressed={periods.includes(p.key)} onClick={() => togglePeriod(p.key)} className={`min-h-[44px] rounded-xl border px-3 py-2 text-left text-sm transition-colors ${periods.includes(p.key) ? 'border-[#BFA05F] bg-[#BFA05F]/10 text-[#1A1815]' : 'border-[#E6E2DA] bg-white text-[#635E54] hover:border-[#BFA05F]/50'}`}>{periods.includes(p.key) ? '✓ ' : ''}{ptPT ? p.pt : p.en}</button>)}</div>
            </fieldset>
            <div>
              <p className="mb-2 text-sm font-medium text-[#1A1815]">{ptPT ? 'Limites de horário (opcional)' : 'Specific time limits (optional)'}</p>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <label className="text-xs text-[#635E54]">{ptPT ? 'A partir das' : 'Not before'}<input type="time" value={earliestTime} onChange={e => setEarliestTime(e.target.value)} className="mt-1 w-full rounded-xl border border-[#E6E2DA] bg-white p-3 text-base sm:text-sm text-[#1A1815]" /></label>
                <label className="text-xs text-[#635E54]">{ptPT ? 'Até às' : 'Not after'}<input type="time" value={latestTime} onChange={e => setLatestTime(e.target.value)} className="mt-1 w-full rounded-xl border border-[#E6E2DA] bg-white p-3 text-base sm:text-sm text-[#1A1815]" /></label>
              </div>
              <p className="mt-1 text-xs text-[#635E54]">{ptPT ? 'Estas horas referem-se ao início do atendimento.' : 'These times refer to the appointment start.'}</p>
            </div>
            <label className="block text-sm font-medium text-[#1A1815]">{ptPT ? 'Observações (opcional)' : 'Notes (optional)'}
              <Textarea maxLength={500} rows={2} value={requestNotes} onChange={e => setRequestNotes(e.target.value)} placeholder={ptPT ? 'Ex.: só consigo chegar depois do trabalho.' : 'E.g. I can only arrive after work.'} className="mt-2 border-[#E6E2DA] bg-white text-base sm:text-sm" />
            </label>
            <fieldset className="space-y-3 rounded-xl border border-[#E6E2DA] bg-white p-4">
              <legend className="px-1 text-sm font-medium text-[#1A1815]">{ptPT ? 'Como preferes ser avisada?' : 'How should we notify you?'}</legend>
              <label className="flex cursor-pointer items-start gap-3 text-base sm:text-sm text-[#1A1815]"><input type="checkbox" checked={emailOptIn} onChange={e => setEmailOptIn(e.target.checked)} className="mt-1 h-5 w-5 shrink-0 accent-[#BFA05F]" /><span><strong>Email</strong><span className="block text-xs text-[#635E54]">{user?.email || (ptPT ? 'Não disponível no perfil' : 'Not available in your profile')}</span></span></label>
              <label className="flex cursor-pointer items-start gap-3 text-base sm:text-sm text-[#1A1815]"><input type="checkbox" checked={whatsappOptIn} onChange={e => setWhatsappOptIn(e.target.checked)} className="mt-1 h-5 w-5 shrink-0 accent-[#BFA05F]" /><span><strong>WhatsApp</strong><span className="block text-xs text-[#635E54]">{ptPT ? 'Autorizo o envio de avisos relacionados com este pedido.' : 'I agree to receive notifications related to this request.'} {user?.phone ? `(${user.phone})` : ''}</span></span></label>
              <p className="text-xs text-[#635E54]">{ptPT ? 'Poderás alterar ou cancelar o pedido na área pessoal quando esta estiver disponível. A inscrição não garante uma vaga.' : 'You can manage this request in your account once available. Joining does not guarantee a booking.'}</p>
            </fieldset>
            <div className="flex items-start gap-2 rounded-xl bg-[#BFA05F]/10 p-3 text-xs leading-relaxed text-[#635E54]"><ShieldCheck className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#BFA05F]" /><span>{ptPT ? 'Uma vaga só poderá ser oferecida com antecedência suficiente para responderes e te deslocares. A marcação nunca é confirmada automaticamente.' : 'Offers will respect your response and travel time. An appointment is never automatically confirmed.'}</span></div>
            <div className="sticky bottom-0 z-20 -mx-4 mt-6 flex flex-col gap-2 border-t border-[#E6E2DA] bg-[#F9F8F6] px-4 pt-3 shadow-[0_-6px_16px_rgba(0,0,0,0.04)] sm:-mx-5 sm:flex-row sm:px-5" style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 12px)" }}>
              <Button type="button" variant="outline" onClick={onClose} disabled={saving} className="min-h-[44px] w-full rounded-full sm:flex-1">{ptPT ? 'Cancelar' : 'Cancel'}</Button>
              <Button type="submit" disabled={saving || !!configError || !config || (!emailOptIn && !whatsappOptIn)} className="min-h-[48px] w-full rounded-full bg-[#1A1815] text-white hover:bg-[#2C2924] sm:flex-1">{saving ? (ptPT ? 'A guardar…' : 'Saving…') : (ptPT ? 'Guardar pedido' : 'Save request')}</Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

const BookingPage = () => {
  const [searchParams] = useSearchParams();
  const preselectedService = searchParams.get('service');
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [staff, setStaff] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [selectedServices, setSelectedServices] = useState(preselectedService ? [preselectedService] : []);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [notes, setNotes] = useState('');
  const [acceptedPolicy, setAcceptedPolicy] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [availabilityMap, setAvailabilityMap] = useState({});
  const [overviewMonth, setOverviewMonth] = useState(new Date());
  // A interface fica ativa por defeito após a instalação; definir 'false' para ocultar em produção antes dos testes.
  const waitlistUiEnabled = process.env.REACT_APP_WAITLIST_UI_ENABLED !== 'false';
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const openWaitlist = () => {
    if (selectedServices.length === 0) { toast.error(language === 'pt' ? 'Escolhe pelo menos um serviço.' : 'Choose a service first.'); return; }
    if (selectedServices.length > 6) { toast.error(language === 'pt' ? 'Para a lista de espera, escolhe até 6 serviços.' : 'Choose up to 6 services for the waitlist.'); return; }
    if (filteredStaff.length === 0) { toast.error(language === 'pt' ? 'Não existe um profissional habilitado para todos estes serviços.' : 'No qualified professional can perform all these services.'); return; }
    setWaitlistOpen(true);
  };
  // Dia vermelho: abre diretamente a lista de espera; não é uma reserva normal.
  const handleCalendarDayClick = (date) => {
    if (!waitlistUiEnabled || !date) return;
    const dateKey = format(date, 'yyyy-MM-dd');
    if (availabilityMap[dateKey] !== 'full') return;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (new Date(date).setHours(0, 0, 0, 0) < today.getTime()) return;
    setSelectedDate(date);
    setSelectedSlot(null);
    openWaitlist();
  };
  // ── Verificação de perfil completo ──────────────────────────
  useEffect(() => {
    if (!loading && user) {
      if (!user.phone || user.phone.trim() === '') {
        toast.error(
          language === 'pt'
            ? 'Por favor, complete o seu perfil com um número de telefone para realizar agendamentos.'
            : 'Please complete your profile with a phone number to make bookings.'
        );
        navigate('/profile');
      }
    }
  }, [user, loading, navigate, language]);
  useEffect(() => { window.scrollTo(0, 0); }, [step]);
  useEffect(() => { fetchInitialData(); }, []);
  useEffect(() => {
    if (selectedServices.length > 0 && selectedDate) {
      fetchAvailableSlots();
    } else {
      setAvailableSlots([]);
    }
  }, [selectedServices, selectedDate, selectedStaff]);
  // Auto-seleccionar primeiro staff disponível
  // REGRA 1: Filtrar staff pelos serviços selecionados
  const filteredStaff = useMemo(() => {
    if (selectedServices.length === 0) return staff;
    const selectedServicesObjects = selectedServices.map(id => services.find(s => s.service_id === id)).filter(Boolean);
    // Interseção: staff que pode fazer TODOS os serviços selecionados
    return staff.filter(s => selectedServicesObjects.every(svc => !svc.staff_ids || svc.staff_ids.length === 0 || svc.staff_ids.includes(s.staff_id)));
  }, [staff, services, selectedServices]);
  useEffect(() => {
    if (filteredStaff.length > 0) {
      // Se o staff atual não está na lista filtrada, resetar
      if (selectedStaff && !filteredStaff.find(s => s.staff_id === selectedStaff)) {
        setSelectedStaff(filteredStaff[0].staff_id);
      } else if (!selectedStaff) {
        setSelectedStaff(filteredStaff[0].staff_id);
      }
    }
  }, [filteredStaff]);
  const fetchInitialData = async () => {
    try {
      const [servicesRes, staffRes, categoriesRes] = await Promise.all([
        api.get('/services'),
        api.get('/staff'),
        api.get('/categories'),
      ]);
      setServices(servicesRes.data);
      setStaff(staffRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };
  // ── CORRECÇÃO PRINCIPAL: fetchAvailableSlots ─────────────────
  // Envia total_duration = soma de (duration + buffer_time) de todos
  // os serviços seleccionados, para que o backend reserve o bloco
  // completo e não retorne slots que não cabem todos os serviços.
  const fetchAvailableSlots = async () => {
    if (selectedServices.length === 0) return;
    setSlotsLoading(true);
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      // Calcular duração total incluindo buffer de cada serviço
      const selectedServicesObjects = selectedServices
        .map(id => services.find(s => s.service_id === id))
        .filter(Boolean);
      const totalDurationWithBuffers = selectedServicesObjects.reduce(
        (acc, s) => acc + (s.duration || 60) + (s.buffer_time || 15),
        0
      );
      const params = new URLSearchParams({
        service_id: selectedServices[0],
        date: dateStr,
      });
      if (selectedStaff) params.append('staff_id', selectedStaff);
      // CORRIGIDO: envia total_duration para que o backend reserve
      // espaço suficiente para todos os serviços em sequência
      if (selectedServices.length > 1) {
        params.append('total_duration', String(totalDurationWithBuffers));
      }
      const response = await api.get(`/bookings/available-slots?${params}`);
      // Filtro de segurança: remover slots cujo serviço+buffer termine após o fecho
      const currentStaff = staff.find(s => s.staff_id === selectedStaff);
      let closingMinutes = 20 * 60; // Default 20:00
      if (currentStaff?.custom_hours_enabled && currentStaff?.working_hours) {
        const dayKeys = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
        const daySchedule = currentStaff.working_hours[dayKeys[selectedDate.getDay()]];
        if (daySchedule?.end) {
          const [eh, em] = daySchedule.end.split(':').map(Number);
          closingMinutes = eh * 60 + em;
        }
      }
      const filtered = response.data.filter(slot => {
        const startDate = new Date(slot.start_time);
        const startMinutes = startDate.getUTCHours() * 60 + startDate.getUTCMinutes();
        return startMinutes + totalDurationWithBuffers <= closingMinutes;
      });
      setAvailableSlots(filtered);
    } catch (error) {
      console.error('Error fetching slots:', error);
      setAvailableSlots([]);
    } finally {
      setSlotsLoading(false);
    }
  };
  // ── Dados derivados ──────────────────────────────────────────
  const selectedServicesData = useMemo(() => {
    return selectedServices.map(id => services.find(s => s.service_id === id)).filter(Boolean);
  }, [selectedServices, services]);
  // ── Mapa de disponibilidade mensal (verde/amarelo/vermelho no calendário) ──
  useEffect(() => {
    if (step !== 2 || selectedServicesData.length === 0) return;
    const serviceId = selectedServicesData[0].service_id;
    const effDuration = selectedServicesData.reduce(
      (acc, s) => acc + (s?.duration || 0) + (s?.buffer_time ?? 15), 0
    );
    const y = overviewMonth.getFullYear();
    const m = overviewMonth.getMonth() + 1;
    const params = new URLSearchParams({ service_id: serviceId, year: String(y), month: String(m), total_duration: String(effDuration) });
    if (selectedStaff) params.append('staff_id', selectedStaff);
    let active = true;
    api.get(`/bookings/availability-overview?${params}`)
      .then((r) => { if (active) setAvailabilityMap(r.data || {}); })
      .catch(() => { if (active) setAvailabilityMap({}); });
    return () => { active = false; };
  }, [step, selectedServicesData, selectedStaff, overviewMonth]);
  const availabilityModifiers = useMemo(() => {
    const mk = (status) => Object.keys(availabilityMap)
      .filter((k) => availabilityMap[k] === status)
      .map((k) => { const [yy, mm, dd] = k.split('-').map(Number); return new Date(yy, mm - 1, dd); });
    return { availHigh: mk('high'), availLow: mk('low'), availFull: mk('full') };
  }, [availabilityMap]);
  const totalDuration = useMemo(
    () => selectedServicesData.reduce((acc, s) => acc + (s?.duration || 0), 0),
    [selectedServicesData]
  );
  const totalPrice = useMemo(
    () => selectedServicesData.reduce((acc, s) => acc + (s?.price || 0), 0),
    [selectedServicesData]
  );
  const totalLoyaltyPoints = useMemo(
    () => selectedServicesData.reduce((acc, s) => acc + (s?.loyalty_points || 0), 0),
    [selectedServicesData]
  );
  // Taxa de serviço aplicada apenas ao pagamento online (compensa a comissão do Stripe)
  const ONLINE_FEE_RATE = 0.015;
  const ONLINE_FEE_FIXED = 0.25;
  const onlineFee = useMemo(
    () => (totalPrice > 0 ? Math.round((totalPrice * ONLINE_FEE_RATE + ONLINE_FEE_FIXED) * 100) / 100 : 0),
    [totalPrice]
  );
  const totalWithFee = useMemo(
    () => Math.round((totalPrice + onlineFee) * 100) / 100,
    [totalPrice, onlineFee]
  );
  const filteredServices = useMemo(() => {
    if (selectedCategory === 'all') return services;
    return services.filter(s => s.category === selectedCategory);
  }, [services, selectedCategory]);
  const toggleService = (serviceId) => {
    setSelectedServices(prev =>
      prev.includes(serviceId) ? prev.filter(id => id !== serviceId) : [...prev, serviceId]
    );
    // Limpar slot seleccionado ao mudar serviços
    setSelectedSlot(null);
  };
  // ── CORRECÇÃO PRINCIPAL: handleSubmit ────────────────────────
  // O próximo start_time de cada serviço deve ser calculado somando
  // duration + buffer_time do serviço anterior (não só duration),
  // caso contrário o backend detecta conflito de sobreposição.
  const [showPayment, setShowPayment] = useState(false);
  const [pendingBookings, setPendingBookings] = useState([]);
  const [paymentChoice, setPaymentChoice] = useState('in_store'); // 'in_store' | 'online'
  const [submitting, setSubmitting] = useState(false);
  const buildBookingsPayload = (method) => {
    let currentStartTime = selectedSlot;
    return selectedServices.map((serviceId, index) => {
      const service = services.find(s => s.service_id === serviceId);
      const booking = {
        service_id: serviceId,
        staff_id: selectedStaff,
        start_time: currentStartTime,
        payment_method: method,
        notes: index === 0 ? (notes || null) : null,
      };
      if (service) {
        const startDate = new Date(currentStartTime);
        startDate.setMinutes(startDate.getMinutes() + (service.duration || 60) + (service.buffer_time || 15));
        currentStartTime = startDate.toISOString();
      }
      return booking;
    });
  };
  const handlePaymentSuccess = async (paymentIntent) => {
    try {
      if (paymentIntent?.id) {
        await api.get(`/payments/intent-status/${paymentIntent.id}`);
      }
    } catch (e) {
      // O webhook fará a finalização caso o polling falhe
    }
    setShowPayment(false);
    toast.success(
      selectedServices.length > 1
        ? `${selectedServices.length} serviços agendados e pagos com sucesso!`
        : 'Marcação paga e confirmada!'
    );
    navigate('/dashboard');
  };
  const handleSubmit = async () => {
    if (!acceptedPolicy) {
      toast.error('Por favor, aceite a política de cancelamento para continuar.');
      return;
    }
    if (paymentChoice === 'online') {
      // Abrir pagamento online (Stripe) — as marcações só são criadas após o pagamento
      setPendingBookings(buildBookingsPayload('online'));
      setShowPayment(true);
      return;
    }
    // Pagar no estabelecimento — cria as marcações imediatamente
    setSubmitting(true);
    try {
      await api.post('/bookings/bulk', { bookings: buildBookingsPayload('in_store') });
      toast.success(
        selectedServices.length > 1
          ? `${selectedServices.length} serviços agendados com sucesso! Pague no estabelecimento.`
          : 'Marcação confirmada! Pague no estabelecimento.'
      );
      navigate('/dashboard');
    } catch (error) {
      console.error('Error creating booking:', error);
      toast.error(error.response?.data?.detail || t('common.error'));
    } finally {
      setSubmitting(false);
    }
  };
  const formatSlotTime = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleTimeString('pt-PT', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Europe/Lisbon',
    });
  };
  const getCategoryName = (slug) => {
    const cat = categories.find(c => c.slug === slug);
    return cat ? (language === 'pt' ? cat.name_pt : cat.name_en) : slug;
  };
  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F8F6] pt-24 flex items-center justify-center">
        <div className="animate-pulse text-[#BFA05F]">{t('common.loading')}</div>
      </div>
    );
  }
  return (
    <div className="min-h-[100svh] min-w-0 overflow-x-clip bg-[#F9F8F6] pt-24 pb-44 sm:pb-36" data-testid="booking-page">
      <div className="mx-auto w-full min-w-0 max-w-4xl px-4 sm:px-6">
        {/* Header */}
        <div className="mb-8 animate-fade-in-up">
          <div className="flex items-center justify-between mb-4">
            <Button
              variant="ghost"
              onClick={() => step > 1 ? setStep(step - 1) : navigate('/services')}
              className="text-[#635E54] hover:text-[#1A1815]"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t('common.back')}
            </Button>
          </div>
          <h1 className="font-heading text-2xl font-medium text-[#1A1815] sm:text-3xl md:text-4xl">
            {t('booking.title')}
          </h1>
          {/* Progress */}
          <div className="flex items-center gap-2 mt-6">
            {[1, 2, 3, 4].map((s) => (
              <div key={s} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                  step >= s ? 'bg-[#1A1815] text-white' : 'bg-[#E6E2DA] text-[#635E54]'
                }`}>
                  {step > s ? <Check className="w-4 h-4" /> : s}
                </div>
                {s < 4 && <div className={`w-8 h-1 ${step > s ? 'bg-[#1A1815]' : 'bg-[#E6E2DA]'}`} />}
              </div>
            ))}
          </div>
        </div>
        {/* ── Step 1: Seleccionar Serviços ── */}
        {step === 1 && (
          <div className="space-y-4 animate-fade-in-up" data-testid="step-service">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
              <h2 className="font-heading text-xl font-medium text-[#1A1815]">
                {language === 'pt' ? 'Agende sua transformação' : 'Select Services'}
              </h2>
              <p className="text-sm text-[#635E54]">
                {language === 'pt' ? 'Você pode selecionar uma ou mais' : 'You can select one or more'}
              </p>
            </div>
            {/* Filtro por Categoria */}
            <div className="flex flex-wrap gap-2 mb-6">
              <button
                onClick={() => setSelectedCategory('all')}
                className={`px-4 py-2 rounded-full text-sm transition-colors ${
                  selectedCategory === 'all' ? 'bg-[#1A1815] text-white' : 'bg-white border border-[#E6E2DA] hover:border-[#BFA05F]'
                }`}
              >
                <Filter className="w-4 h-4 inline mr-2" />
                {language === 'pt' ? 'Todos' : 'All'}
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.category_id}
                  onClick={() => setSelectedCategory(cat.slug)}
                  className={`px-4 py-2 rounded-full text-sm transition-colors ${
                    selectedCategory === cat.slug ? 'bg-[#1A1815] text-white' : 'bg-white border border-[#E6E2DA] hover:border-[#BFA05F]'
                  }`}
                >
                  {language === 'pt' ? cat.name_pt : cat.name_en}
                </button>
              ))}
            </div>
            {/* Lista de Serviços */}
            <div className="grid gap-3">
              {filteredServices.map((service) => {
                const isSelected = selectedServices.includes(service.service_id);
                return (
                  <div
                    key={service.service_id}
                    onClick={() => toggleService(service.service_id)}
                    className={`flex items-center justify-between p-4 rounded-xl border-2 transition-all cursor-pointer ${
                      isSelected ? 'border-[#BFA05F] bg-[#BFA05F]/5' : 'border-[#E6E2DA] bg-white hover:border-[#BFA05F]/50'
                    }`}
                    data-testid={`service-${service.service_id}`}
                  >
                    <div className="flex items-center gap-4">
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => toggleService(service.service_id)}
                        className="w-5 h-5 rounded-sm border-[#E6E2DA] data-[state=checked]:bg-[#BFA05F] data-[state=checked]:border-[#BFA05F]"
                        onClick={(e) => e.stopPropagation()}
                      />
                      <div className="text-left">
                        <h3 className="font-medium text-[#1A1815]">
                          {language === 'pt' ? service.name_pt : service.name_en}
                        </h3>
                        <div className="flex items-center gap-3 mt-1 text-sm text-[#635E54]">
                          <span className="px-2 py-0.5 bg-[#F2F0EB] rounded text-xs">
                            {getCategoryName(service.category)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" /> {service.duration}min
                          </span>
                          {service.loyalty_points > 0 && (
                            <span className="flex items-center gap-1 text-[#BFA05F] font-semibold text-xs">
                              <Star className="w-3 h-3 fill-[#BFA05F]" /> {service.loyalty_points} pts
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <span className="font-bold text-[#BFA05F] text-lg">€{service.price}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {/* ── Step 2: Staff & Data ── */}
        {step === 2 && (
          <div className="space-y-8 animate-fade-in-up" data-testid="step-date">
            <div>
              <h2 className="font-heading text-xl font-medium text-[#1A1815] mb-4">
                {t('booking.select_staff')}
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {filteredStaff.map((s) => (
                  <button
                    key={s.staff_id}
                    onClick={() => setSelectedStaff(s.staff_id)}
                    className={`p-4 rounded-2xl border text-center transition-all ${
                      selectedStaff === s.staff_id ? 'border-[#BFA05F] bg-[#BFA05F]/5' : 'border-[#E6E2DA] bg-white hover:border-[#BFA05F]/50'
                    }`}
                    data-testid={`staff-${s.staff_id}`}
                  >
                    <img
                      src={s.photo || 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=100'}
                      alt={s.name}
                      className="w-12 h-12 rounded-full object-cover mx-auto mb-2"
                    />
                    <p className="text-sm font-medium text-[#1A1815]">{s.name}</p>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <h2 className="font-heading text-xl font-medium text-[#1A1815] mb-4">
                {t('booking.select_date')}
              </h2>
              <div className="inline-block w-full max-w-full overflow-x-auto rounded-2xl border border-[#E6E2DA] bg-white p-2 sm:w-auto sm:p-6">
                <CalendarComponent
                  mode="single"
                  selected={selectedDate}
                  month={overviewMonth}
                  onMonthChange={setOverviewMonth}
                  modifiers={availabilityModifiers}
                  modifiersClassNames={{
                    availHigh: '!bg-green-500/25 !rounded-full !text-[#1A1815]',
                    availLow: '!bg-yellow-500/30 !rounded-full !text-[#1A1815]',
                    availFull: '!bg-red-500/25 !rounded-full !text-[#1A1815]',
                  }}
                  onSelect={(date) => {
                    if (!date) return;
                    // O dia completo não pode avançar como marcação; o onDayClick abre a lista.
                    if (waitlistUiEnabled && availabilityMap[format(date, 'yyyy-MM-dd')] === 'full') return;
                    setSelectedDate(date);
                    setSelectedSlot(null);
                  }}
                  onDayClick={handleCalendarDayClick}
                  disabled={(date) => {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    const checkDate = new Date(date);
                    checkDate.setHours(0, 0, 0, 0);
                    if (checkDate < today) return true;
                    const dateKey = format(date, 'yyyy-MM-dd');
                    // Vermelho é sempre clicável com a lista ativa, mesmo que não haja um único horário.
                    if (availabilityMap[dateKey] === 'full') return !waitlistUiEnabled;
                    // Verificar se o staff selecionado trabalha neste dia da semana
                    const dayMap = [6, 0, 1, 2, 3, 4, 5]; // JS getDay: 0=Sun→6, 1=Mon→0, ...
                    const dayKeys = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
                    const dayKey = dayKeys[dayMap[date.getDay()]];
                    const currentStaff = staff.find(s => s.staff_id === selectedStaff);
                    if (currentStaff && currentStaff.custom_hours_enabled && currentStaff.working_hours) {
                      const daySchedule = currentStaff.working_hours[dayKey];
                      if (daySchedule && !daySchedule.enabled) return true;
                    } else {
                      // Horário padrão do salão: seg-sáb (bloquear domingo)
                      if (date.getDay() === 0) return true;
                    }
                    return false;
                  }}
                  locale={language === 'pt' ? pt : enGB}
                  className="rounded-xl"
                  data-testid="calendar"
                />
              </div>
              <div className="flex flex-wrap items-center gap-4 mt-4 text-xs text-[#635E54]" data-testid="availability-legend">
                <span className="flex items-center gap-1.5">
                  <span className="w-3.5 h-3.5 rounded-full" style={{ backgroundColor: 'rgba(34,197,94,0.30)' }} />
                  {language === 'pt' ? 'Muita disponibilidade' : 'Plenty available'}
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-3.5 h-3.5 rounded-full" style={{ backgroundColor: 'rgba(234,179,8,0.40)' }} />
                  {language === 'pt' ? 'Poucos horários' : 'Few slots'}
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-3.5 h-3.5 rounded-full" style={{ backgroundColor: 'rgba(239,68,68,0.35)' }} />
                  {language === 'pt' ? 'Indisponível' : 'Fully booked'}
                </span>
              </div>
              {waitlistUiEnabled && selectedDate && availabilityMap[format(selectedDate, 'yyyy-MM-dd')] === 'full' && (
                <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-900" data-testid="waitlist-full-date-info">
                  {language === 'pt' ? 'Este dia está esgotado. Não é possível fazer uma marcação normal, mas podes pedir para ser avisada se surgir uma vaga.' : 'This date is fully booked. You can join the waitlist instead of making a regular booking.'}
                  <Button type="button" onClick={openWaitlist} className="ml-0 mt-3 min-h-[44px] w-full rounded-full bg-[#1A1815] text-white hover:bg-[#2C2924] sm:w-auto"><BellRing className="mr-2 inline h-4 w-4" />{language === 'pt' ? 'Pedir vaga neste dia' : 'Request a slot on this date'}</Button>
                </div>
              )}
              {waitlistUiEnabled && (
                <div className="mt-5 rounded-2xl border border-[#BFA05F]/30 bg-[#BFA05F]/5 p-4" data-testid="waitlist-step-date">
                  <div className="flex items-start gap-3"><CalendarClock className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#BFA05F]" /><div className="flex-1"><p className="font-medium text-[#1A1815]">{language === 'pt' ? 'Não encontras uma data que te sirva?' : 'Can’t find a suitable date?'}</p><p className="mt-1 text-sm text-[#635E54]">{language === 'pt' ? 'Mesmo com dias disponíveis, podes indicar os dias e horários em que consegues vir.' : 'Even if some days are available, tell us which dates and times work for you.'}</p></div></div>
                  <Button type="button" variant="outline" onClick={openWaitlist} className="mt-3 min-h-[44px] w-full rounded-full border-[#BFA05F] text-[#1A1815] hover:bg-[#BFA05F]/10 sm:w-auto"><BellRing className="mr-2 h-4 w-4" />{language === 'pt' ? 'Entrar na lista de espera' : 'Join the waitlist'}</Button>
                </div>
              )}
            </div>
          </div>
        )}
        {/* ── Step 3: Seleccionar Hora ── */}
        {step === 3 && (
          <div className="space-y-6 animate-fade-in-up" data-testid="step-time">
            <h2 className="font-heading text-xl font-medium text-[#1A1815] mb-4">
              {t('booking.select_time')} — {selectedDate && format(selectedDate, 'dd MMMM yyyy', { locale: language === 'pt' ? pt : enGB })}
            </h2>
            {/* Aviso quando há múltiplos serviços */}
            {selectedServices.length > 1 && (
              <div className="bg-[#BFA05F]/10 border border-[#BFA05F]/30 rounded-xl p-3 flex items-start gap-2">
                <Clock className="w-4 h-4 text-[#BFA05F] mt-0.5 flex-shrink-0" />
                <p className="text-xs text-[#635E54]">
                  {language === 'pt'
                    ? `Os ${selectedServices.length} serviços serão realizados em sequência. Os horários mostrados têm espaço para todos.`
                    : `The ${selectedServices.length} services will be performed in sequence. Shown times have space for all of them.`}
                </p>
              </div>
            )}
            {slotsLoading ? (
              <div className="text-[#635E54] flex items-center gap-2">
                <div className="animate-spin w-4 h-4 border-2 border-[#BFA05F] border-t-transparent rounded-full" />
                {t('common.loading')}
              </div>
            ) : availableSlots.length === 0 ? (
              <div className="bg-white rounded-2xl p-8 text-center border border-[#E6E2DA]">
                <Clock className="w-12 h-12 text-[#D1CCC4] mx-auto mb-4" />
                <p className="text-[#635E54]">{t('booking.no_slots')}</p>
                <Button variant="outline" onClick={() => setStep(2)} className="mt-4 rounded-full">
                  {t('common.back')}
                </Button>
                {waitlistUiEnabled && <div className="mt-4"><Button type="button" onClick={openWaitlist} className="min-h-[44px] w-full rounded-full bg-[#1A1815] text-white hover:bg-[#2C2924] sm:w-auto"><BellRing className="mr-2 h-4 w-4" />{language === 'pt' ? 'Entrar na lista de espera' : 'Join the waitlist'}</Button></div>}
              </div>
            ) : (
              <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
                {availableSlots.map((slot, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedSlot(slot.start_time)}
                    className={`min-h-[44px] min-w-0 rounded-xl border px-2 py-3 text-center text-sm transition-all sm:px-4 ${
                      selectedSlot === slot.start_time
                        ? 'border-[#BFA05F] bg-[#BFA05F] text-white'
                        : 'border-[#E6E2DA] bg-white hover:border-[#BFA05F]/50 text-[#1A1815]'
                    }`}
                    data-testid={`slot-${index}`}
                  >
                    {formatSlotTime(slot.start_time)}
                  </button>
                ))}
              </div>
            )}
            {waitlistUiEnabled && availableSlots.length > 0 && !slotsLoading && (
              <div className="rounded-2xl border border-[#BFA05F]/30 bg-white p-4" data-testid="waitlist-step-time">
                <p className="text-sm text-[#635E54]">{language === 'pt' ? 'Os horários disponíveis não te servem? Podes indicar outros dias e horas.' : 'None of these times work? You can request different dates and hours.'}</p>
                <Button type="button" variant="outline" onClick={openWaitlist} className="mt-3 min-h-[44px] w-full rounded-full border-[#BFA05F] sm:w-auto"><BellRing className="mr-2 h-4 w-4" />{language === 'pt' ? 'Entrar na lista de espera' : 'Join the waitlist'}</Button>
              </div>
            )}
          </div>
        )}
        {/* ── Step 4: Confirmar ── */}
        {step === 4 && (
          <div className="space-y-6 animate-fade-in-up" data-testid="step-confirm">
            <div className="min-w-0 rounded-2xl border border-[#E6E2DA] bg-white p-4 sm:p-6">
              <h2 className="font-heading text-xl font-medium text-[#1A1815] mb-4">
                {language === 'pt' ? 'Resumo da Marcação' : 'Booking Summary'}
              </h2>
              <div className="space-y-4">
                {selectedServicesData.map((service) => (
                  <div key={service.service_id} className="flex items-center gap-4 pb-4 border-b border-[#E6E2DA] last:border-0">
                    <img
                      src={service.images?.[0] || 'https://images.pexels.com/photos/5659012/pexels-photo-5659012.jpeg'}
                      alt=""
                      className="w-16 h-16 rounded-xl object-cover"
                      onError={(e) => { e.target.src = 'https://images.pexels.com/photos/5659012/pexels-photo-5659012.jpeg'; }}
                    />
                    <div className="flex-1">
                      <h3 className="font-medium text-[#1A1815]">
                        {language === 'pt' ? service.name_pt : service.name_en}
                      </h3>
                      <div className="flex items-center justify-between mt-1">
                        <span className="flex items-center gap-1 text-sm text-[#635E54]">
                          <Clock className="w-3 h-3" /> {service.duration}min
                        </span>
                        <div className="text-right">
                          <span className="font-bold text-[#BFA05F] block">€{service.price}</span>
                          {service.loyalty_points > 0 && (
                            <span className="text-[10px] text-[#BFA05F] font-bold flex items-center gap-1 justify-end">
                              <Star className="w-2.5 h-2.5 fill-[#BFA05F]" /> +{service.loyalty_points} pts
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-4 border-t border-[#E6E2DA]">
                <div className="flex items-center gap-2 text-[#1A1815]">
                  <Calendar className="w-5 h-5 text-[#BFA05F]" />
                  <span className="font-medium">
                    {selectedDate && format(selectedDate, 'EEEE, dd MMMM yyyy', { locale: language === 'pt' ? pt : enGB })}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-[#1A1815] mt-2">
                  <Clock className="w-5 h-5 text-[#BFA05F]" />
                  <span>{selectedSlot && formatSlotTime(selectedSlot)} • {totalDuration} min total</span>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-[#E6E2DA]">
                <div className="flex justify-between items-end">
                  <div>
                    <span className="text-[#635E54] text-xs block mb-1">
                      {language === 'pt' ? 'Irá ganhar nesta reserva:' : 'You will earn on this booking:'}
                    </span>
                    <div className="inline-flex items-center gap-1.5 bg-[#BFA05F]/10 text-[#BFA05F] px-3 py-1 rounded-full text-xs font-bold border border-[#BFA05F]/20">
                      <Star className="w-3.5 h-3.5 fill-[#BFA05F]" />
                      {totalLoyaltyPoints} {language === 'pt' ? 'Pontos de Fidelidade' : 'Loyalty Points'}
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-[#635E54] text-xs block mb-1">
                      {language === 'pt' ? 'Total' : 'Total'}
                    </span>
                    <span className="text-2xl font-bold text-[#1A1815]">€{totalPrice.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>
            {/* Método de Pagamento */}
            <div className="min-w-0 rounded-2xl border border-[#E6E2DA] bg-white p-4 sm:p-6" data-testid="payment-method-section">
              <h2 className="font-heading text-lg font-medium text-[#1A1815] mb-4">
                {language === 'pt' ? 'Como pretende pagar?' : 'How would you like to pay?'}
              </h2>
              <div className="grid gap-3 md:grid-cols-2">
                <button
                  type="button"
                  onClick={() => setPaymentChoice('in_store')}
                  data-testid="pay-in-store-option"
                  className={`flex items-start gap-3 p-4 rounded-xl border-2 text-left transition-all ${
                    paymentChoice === 'in_store' ? 'border-[#BFA05F] bg-[#BFA05F]/5' : 'border-[#E6E2DA] bg-white hover:border-[#BFA05F]/50'
                  }`}
                >
                  <Store className={`w-6 h-6 flex-shrink-0 mt-0.5 ${paymentChoice === 'in_store' ? 'text-[#BFA05F]' : 'text-[#635E54]'}`} />
                  <div>
                    <p className="font-medium text-[#1A1815]">
                      {language === 'pt' ? 'Pagar no estabelecimento' : 'Pay at the venue'}
                    </p>
                    <p className="text-xs text-[#635E54] mt-1">
                      {language === 'pt' ? 'Dinheiro ou multibanco no dia da marcação.' : 'Cash or card on the day of your appointment.'}
                    </p>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setPaymentChoice('online')}
                  data-testid="pay-online-option"
                  className={`flex items-start gap-3 p-4 rounded-xl border-2 text-left transition-all ${
                    paymentChoice === 'online' ? 'border-[#BFA05F] bg-[#BFA05F]/5' : 'border-[#E6E2DA] bg-white hover:border-[#BFA05F]/50'
                  }`}
                >
                  <CreditCard className={`w-6 h-6 flex-shrink-0 mt-0.5 ${paymentChoice === 'online' ? 'text-[#BFA05F]' : 'text-[#635E54]'}`} />
                  <div>
                    <p className="font-medium text-[#1A1815]">
                      {language === 'pt' ? 'Pagar agora online' : 'Pay now online'}
                    </p>
                    <p className="text-xs text-[#635E54] mt-1">
                      {language === 'pt' ? 'Cartão, MB WAY, Klarna e mais — seguro via Stripe.' : 'Card, MB WAY, Klarna and more — securely via Stripe.'}
                    </p>
                  </div>
                </button>
              </div>
              {paymentChoice === 'online' && totalPrice > 0 && (
                <div className="mt-4 rounded-xl bg-[#F9F8F6] border border-[#E6E2DA] p-4 space-y-2" data-testid="online-fee-breakdown">
                  <div className="flex items-center justify-between text-sm text-[#635E54]">
                    <span>{language === 'pt' ? 'Subtotal dos serviços' : 'Services subtotal'}</span>
                    <span data-testid="fee-subtotal">€{totalPrice.toFixed(2)}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm text-[#635E54]">
                    <span>{language === 'pt' ? 'Taxa de serviço' : 'Service fee'} *</span>
                    <span data-testid="fee-amount">€{onlineFee.toFixed(2)}</span>
                  </div>
                  <div className="flex items-center justify-between text-base font-medium text-[#1A1815] pt-2 border-t border-[#E6E2DA]">
                    <span>{language === 'pt' ? 'Total a pagar online' : 'Total to pay online'}</span>
                    <span data-testid="fee-total">€{totalWithFee.toFixed(2)}</span>
                  </div>
                  <p className="text-[11px] text-[#A8A29A]" data-testid="fee-note">
                    {language === 'pt'
                      ? '* Valor ajustado devido à taxa de serviço aplicada aos pagamentos online (Stripe). Ao pagar no estabelecimento não há qualquer taxa.'
                      : '* Amount adjusted due to the service fee applied to online payments (Stripe). No fee when paying at the venue.'}
                  </p>
                </div>
              )}
            </div>
            {/* Notas */}
            <div className="min-w-0 rounded-2xl border border-[#E6E2DA] bg-white p-4 sm:p-6">
              <h2 className="font-heading text-lg font-medium text-[#1A1815] mb-3">
                {t('booking.notes')}
              </h2>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={language === 'pt' ? 'Alguma observação especial?' : 'Any special notes?'}
                className="border-[#E6E2DA] focus:border-[#BFA05F] rounded-xl"
                rows={3}
                data-testid="notes-input"
              />
            </div>
            {/* Política de Cancelamento */}
            <div className="bg-amber-50 rounded-2xl p-6 border border-amber-200">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-heading text-lg font-medium text-amber-800 mb-3">
                    {language === 'pt' ? 'Política de Cancelamento e Reagendamento' : 'Cancellation and Rescheduling Policy'}
                  </h3>
                  <div className="text-sm text-amber-900 space-y-3">
                    <p>
                      {language === 'pt'
                        ? 'Ao efetuar a sua marcação, o cliente aceita as seguintes condições:'
                        : 'By making your appointment, you accept the following conditions:'}
                    </p>
                    <p>
                      <strong>{language === 'pt' ? 'Cancelamento Antecipado:' : 'Early Cancellation:'}</strong>{' '}
                      {language === 'pt'
                        ? 'Cancelamentos efetuados com menos de 24 horas de antecedência ou a não comparência (No-Show) conferem ao estabelecimento o direito de cobrar uma taxa de compensação de 30% do valor do serviço.'
                        : 'Cancellations made less than 24 hours in advance or no-shows entitle the establishment to charge a compensation fee of 30% of the service value.'}
                    </p>
                    <p>
                      <strong>{language === 'pt' ? 'Enquadramento Legal:' : 'Legal Framework:'}</strong>{' '}
                      {language === 'pt'
                        ? 'Esta taxa é aplicada a título de cláusula penal, conforme previsto no Artigo 810.º do Código Civil, visando ressarcir o profissional pelos prejuízos decorrentes da vacatura do horário.'
                        : 'This fee is applied as a penalty clause, as provided in Article 810 of the Civil Code, to compensate the professional for losses resulting from the vacancy of the time slot.'}
                    </p>
                    <p>
                      <strong>{language === 'pt' ? 'Dever de Informação:' : 'Duty of Information:'}</strong>{' '}
                      {language === 'pt'
                        ? 'Em conformidade com a Lei de Defesa do Consumidor (Lei n.º 24/96) e o Decreto-Lei n.º 24/2014, o cliente é informado desta condição antes da confirmação da reserva, constituindo a finalização do agendamento uma aceitação plena destes termos.'
                        : 'In accordance with the Consumer Protection Law (Law No. 24/96) and Decree-Law No. 24/2014, the client is informed of this condition before booking confirmation, with the finalization of the appointment constituting full acceptance of these terms.'}
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3 mt-6 pt-4 border-t border-amber-200">
                <Checkbox
                  id="accept-policy"
                  checked={acceptedPolicy}
                  onCheckedChange={setAcceptedPolicy}
                  className="border-amber-400 data-[state=checked]:bg-amber-600"
                />
                <label htmlFor="accept-policy" className="text-sm font-medium text-amber-900 cursor-pointer">
                  {language === 'pt'
                    ? 'Li e aceito a política de cancelamento e reagendamento'
                    : 'I have read and accept the cancellation and rescheduling policy'}
                </label>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Sticky Footer */}
      <div className="fixed inset-x-0 bottom-0 z-50 border-t border-[#E6E2DA] bg-white px-3 pt-3 shadow-[0_-4px_10px_rgba(0,0,0,0.05)] sm:px-4 sm:pt-4 md:p-6" style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 24px)" }}>
        <div className="mx-auto flex w-full max-w-4xl min-w-0 items-center justify-between gap-2 sm:gap-4">
          <div className="min-w-0 flex-1">
            {selectedServices.length > 0 ? (
              <div className="flex flex-col">
                <div className="flex items-center gap-2">
                  <span className="text-lg md:text-xl font-bold text-[#1A1815]">€{(paymentChoice === 'online' ? totalWithFee : totalPrice).toFixed(2)}</span>
                  <span className="text-[10px] md:text-xs font-bold text-[#BFA05F] flex items-center gap-0.5">
                    <Star className="w-3 h-3 fill-[#BFA05F]" /> +{totalLoyaltyPoints} PTS
                  </span>
                </div>
                <p className="text-[10px] md:text-xs text-[#635E54]">
                  {selectedServices.length} {language === 'pt' ? 'serviço(s)' : 'service(s)'} • {totalDuration} min
                  {paymentChoice === 'online' && totalPrice > 0 && (
                    <span className="text-[#BFA05F]"> • {language === 'pt' ? `inclui €${onlineFee.toFixed(2)} de taxa` : `incl. €${onlineFee.toFixed(2)} fee`}</span>
                  )}
                </p>
              </div>
            ) : (
              <p className="text-sm text-[#635E54]">
                {language === 'pt' ? '0 transformações selecionadas' : 'Select a service'}
              </p>
            )}
          </div>
          <div className="shrink-0">
            {step === 1 && (
              <Button
                onClick={() => setStep(2)}
                disabled={selectedServices.length === 0}
                className="bg-[#1A1815] text-white hover:bg-[#2C2924] rounded-full px-4 sm:px-6 md:px-10 py-6"
              >
                {t('common.next')} <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            )}
            {step === 2 && (
              <Button
                onClick={() => setStep(3)}
                disabled={!selectedDate || availabilityMap[format(selectedDate, 'yyyy-MM-dd')] === 'full'}
                className="bg-[#1A1815] text-white hover:bg-[#2C2924] rounded-full px-4 sm:px-6 md:px-10 py-6"
              >
                {t('common.next')} <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            )}
            {step === 3 && (
              <Button
                onClick={() => setStep(4)}
                disabled={!selectedSlot}
                className="bg-[#1A1815] text-white hover:bg-[#2C2924] rounded-full px-4 sm:px-6 md:px-10 py-6"
              >
                {t('common.next')} <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            )}
            {step === 4 && (
              <Button
                onClick={handleSubmit}
                disabled={!acceptedPolicy || submitting}
                data-testid="confirm-booking-btn"
                className={`rounded-full px-4 sm:px-6 md:px-10 py-6 text-sm sm:text-base transition-all ${
                  acceptedPolicy && !submitting
                    ? 'bg-[#1A1815] text-white hover:bg-[#BFA05F]'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                {submitting
                  ? (language === 'pt' ? 'A processar...' : 'Processing...')
                  : paymentChoice === 'online'
                    ? (language === 'pt' ? 'Ir para pagamento' : 'Go to payment')
                    : t('booking.confirm')}
                {!submitting && <Check className="w-4 h-4 ml-2" />}
              </Button>
            )}
          </div>
        </div>
      </div>
      <StripePaymentModal
        open={showPayment}
        onClose={() => setShowPayment(false)}
        bookingsPayload={pendingBookings}
        onSuccess={handlePaymentSuccess}
      />
      {waitlistUiEnabled && (
        <WaitlistJoinModal
          open={waitlistOpen}
          onClose={() => setWaitlistOpen(false)}
          selectedServices={selectedServicesData}
          selectedStaff={selectedStaff}
          selectedDate={selectedDate}
          eligibleStaff={filteredStaff}
          user={user}
          language={language}
        />
      )}
    </div>
  );
};
export default BookingPage;
